// config.js
// -----------------------------------------------------------------------------
// The ONE place the app reads build-time configuration. Everything here comes from
// `import.meta.env`, which Vite populates from the `.env.<mode>` file matching the build
// mode and compiles into the bundle.
//
// REPLACES THE RUNTIME `public/env.js`. That file was a `window.env = {...}` script placed
// on each environment's storage account by hand and deliberately excluded from the
// front-end deploy's azcopy sync, so it was the one piece of configuration that lived
// nowhere in source control: nobody could review it, a new environment silently served a
// broken app until someone remembered to upload it, and the values could drift from the
// API's own appsettings without anything noticing. Now each environment's configuration is
// a committed file selected by `--mode`.
//
// MODES (see package.json for the matching `build:*` scripts):
//   development     local dev — `yarn start`
//   test            vitest — `.env.test`
//   wk-staging      WK staging
//   wk-production   WK production
//   met-staging     Met staging
//   met-production  Met production
//   ocm-staging     Oak Crest staging
//   ocm-production  Oak Crest production
//
// NOTHING SECRET GOES IN THOSE FILES. Every `VITE_`-prefixed value is compiled into the
// client bundle and readable by any visitor, so these are all public values by nature —
// the Entra ID client ID, authority and API scope are already exposed in any SPA that signs
// users in, and the API's own copies are committed in appsettings.<Environment>.json. A
// genuine secret would have to be held by the API, never here.
// -----------------------------------------------------------------------------

// Every value the app cannot run without. Checked here so a misconfigured bundle fails
// immediately, with the variable name and the file to fix, rather than surfacing later as
// `undefined` in an MSAL error or a request to `undefined/v1/companies`.
//
// vite.config.js runs the same check at BUILD time (see `requireEnvPlugin`), so a missing
// value normally fails the build long before this runs. This is the backstop for the case
// the plugin cannot cover — a bundle built by some other path, or a mode added to one place
// and not the other.
const REQUIRED_KEYS = [
  "VITE_APP_NAME",
  "VITE_API_BASE",
  "VITE_AZURE_CLIENT_ID",
  "VITE_AZURE_AUTHORITY",
  "VITE_API_SCOPES",
];

const missingKeys = REQUIRED_KEYS.filter(
  (key) => !import.meta.env[key] || String(import.meta.env[key]).trim() === ""
);

if (missingKeys.length > 0) {
  throw new Error(
    `Missing front-end configuration: ${missingKeys.join(", ")}. ` +
      `The build ran in mode '${import.meta.env.MODE}', so these belong in ` +
      `src/front-end/.env.${import.meta.env.MODE}. ` +
      `See src/front-end/src/config.js for what each one means.`
  );
}

// The name shown in the app header and the browser tab. This is what tells environments
// apart now that the green "staging" heading style is gone, so give staging a name that is
// obviously not production.
export const appName = import.meta.env.VITE_APP_NAME;

// The build mode, exposed so the UI can state which configuration it was built with. Not a
// substitute for appName — this is diagnostic, appName is what users read.
export const buildMode = import.meta.env.MODE;

export const squireApiConfig = {
  // Base URL every API call is prefixed with. A PATH (`/api`) for deployed environments,
  // because Front Door routes `/api/*` on the SPA's own hostname to the App Service — so
  // the API is same-origin and no CORS is involved. An absolute URL in development, where
  // the API runs on its own Kestrel port.
  url: import.meta.env.VITE_API_BASE,

  // Scopes requested when acquiring an access token for the API. Comma-separated in the
  // .env file because a .env value is always a string; MSAL wants an array.
  scopes: import.meta.env.VITE_API_SCOPES.split(",")
    .map((scope) => scope.trim())
    .filter(Boolean),
};

export const azureAdConfig = {
  // Application (client) ID of the SPA's Entra ID app registration.
  clientId: import.meta.env.VITE_AZURE_CLIENT_ID,

  // Full authority URL, including the tenant: https://login.microsoftonline.com/<tenant>.
  // Per company — WK and Met are separate tenants.
  authority: import.meta.env.VITE_AZURE_AUTHORITY,
};
